import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { http } from "../../components/helper";

// Fetch CMS content for a page
export const fetchVendorLandingContent = createAsyncThunk(
  "cms/fetchVendorLandingContent",
  async (pageId = "vendor-landing", { rejectWithValue }) => {
    try {
      const response = await http.get(`/public/cms/${pageId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update CMS content
export const updateVendorLandingContent = createAsyncThunk(
  "cms/updateVendorLandingContent",
  async ({ pageId = "vendor-landing", content }, { rejectWithValue }) => {
    try {
      const response = await http.put(`/admin/cms/${pageId}`, content);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update specific section
export const updateCMSSection = createAsyncThunk(
  "cms/updateCMSSection",
  async ({ pageId = "vendor-landing", sectionName, sectionData }, { rejectWithValue }) => {
    try {
      const response = await http.put(
        `/admin/cms/${pageId}/section/${sectionName}`,
        sectionData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete section
export const deleteSection = createAsyncThunk(
  "cms/deleteSection",
  async ({ pageId = "vendor-landing", sectionName }, { rejectWithValue }) => {
    try {
      const response = await http.delete(
        `/admin/cms/${pageId}/section/${sectionName}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
//fetch homepagebanner
export const fetchHomepageBanner = createAsyncThunk(
  "cms/fetchHomepageBanner",
  async (pageId = "vendor-landing", { rejectWithValue }) => {
    try {
      const response = await http.get(`/public/cms/${pageId}/section/homepagebanner`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  vendorLandingContent: null,
  homepageBanner: null,
  status: "idle",
  error: null,
  editingSection: null,
  saveSuccess: false,
};

const cmsSlice = createSlice({
  name: "cms",
  initialState,
  reducers: {
    setEditingSection: (state, action) => {
      state.editingSection = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetSaveSuccess: (state) => {
      state.saveSuccess = false;
    },
    updateLocalContent: (state, action) => {
      const { sectionName, field, value } = action.payload;
      if (state.vendorLandingContent?.sections) {
        state.vendorLandingContent.sections[sectionName] = {
          ...state.vendorLandingContent.sections[sectionName],
          [field]: value,
        };
      }
    },
    updateLocalSectionArray: (state, action) => {
      const { sectionName, arrayName, index, value } = action.payload;
      if (state.vendorLandingContent?.sections?.[sectionName]) {
        const section = state.vendorLandingContent.sections[sectionName];
        if (!section[arrayName]) {
          section[arrayName] = [];
        }
        section[arrayName][index] = {
          ...section[arrayName][index],
          ...value,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch vendor landing content
      .addCase(fetchVendorLandingContent.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchVendorLandingContent.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.vendorLandingContent = action.payload.data;
        state.error = null;
      })
      .addCase(fetchVendorLandingContent.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update vendor landing content
      .addCase(updateVendorLandingContent.pending, (state) => {
        state.status = "updating";
      })
      .addCase(updateVendorLandingContent.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.vendorLandingContent = action.payload.data;
        state.saveSuccess = true;
        state.error = null;
      })
      .addCase(updateVendorLandingContent.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.saveSuccess = false;
      })

      // Update section
      .addCase(updateCMSSection.pending, (state) => {
        state.status = "updating";
      })
      .addCase(updateCMSSection.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.vendorLandingContent = action.payload.data;
        state.saveSuccess = true;
        state.error = null;
      })
      .addCase(updateCMSSection.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.saveSuccess = false;
      })

      // Delete section
      .addCase(deleteSection.pending, (state) => {
        state.status = "deleting";
      })
      .addCase(deleteSection.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.vendorLandingContent = action.payload.data;
        state.saveSuccess = true;
        state.error = null;
      })
      .addCase(deleteSection.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.saveSuccess = false;
      })
      .addCase(fetchHomepageBanner.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchHomepageBanner.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.homepageBanner = action.payload.data;
      })
      .addCase(fetchHomepageBanner.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const {
  setEditingSection,
  clearError,
  resetSaveSuccess,
  updateLocalContent,
  updateLocalSectionArray,
} = cmsSlice.actions;

export default cmsSlice.reducer;
